import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import type {
  SiteWideTechnicalIssue,
  TechnicalRollup
} from '../../services/technical-rollup.service';

/**
 * Technical issues the site's build put on many pages at once — the ones an
 * operator fixes once rather than once per page.
 *
 * This section exists because the per-page Technical list was mostly not about
 * the page. On `manyrequests.com` 2,597 of 3,172 issue mentions are the same
 * handful of template defects repeated across 336 pages; on `slidespeak.co` it
 * is 3,604 of 4,465. Rendering them per row asked the operator to notice the
 * repetition themselves, across four hundred rows, and then to work out that it
 * was one job.
 *
 * **The rule for what lands here is the server's** (see the backend
 * `technical-issue-taxonomy.ts`), and it is not prevalence. "Page title too
 * long" reaches 207 of 237 blog pages and is deliberately absent, because it is
 * 207 rewrites; "Duplicate meta tags" reaches 242 and is present, because it is
 * one edit to whatever emits the `<head>`. This component renders that decision
 * and never second-guesses it.
 */
@Component({
  selector: 'app-site-wide-issues',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './site-wide-issues.html',
  styleUrl: './site-wide-issues.css'
})
export class SiteWideIssuesComponent {
  /** Already ordered by reach, server-side. Rendered in order. */
  @Input({ required: true }) issues: SiteWideTechnicalIssue[] = [];

  /** For the subhead's totals. Null renders the section without them. */
  @Input() rollup: TechnicalRollup | null = null;

  @Input() heading = 'Site-wide issues';

  trackByCode(_index: number, issue: SiteWideTechnicalIssue): string {
    return issue.code;
  }

  /**
   * The line that makes the section worth its space: how much of the per-page
   * noise these few rows account for.
   *
   * Phrased as findings-cleared rather than as a percentage, because "82% of
   * issue mentions" is a statistic and "clears 2,597 of 3,172 findings across
   * 336 pages" is a reason to do the work this afternoon. Degrades to the count
   * alone when there are no totals to read.
   */
  summary(): string {
    const n = this.issues.length;
    const lead = `${n} ${n === 1 ? 'issue' : 'issues'} built into the site, not written into pages`;
    const totals = this.rollup?.totals;
    if (!totals || totals.issueMentions <= 0) return `${lead}.`;
    return (
      `${lead} — fixing them clears ${totals.siteWideMentions.toLocaleString()} of ` +
      `${totals.issueMentions.toLocaleString()} findings across ` +
      `${(this.rollup?.analysedPageCount ?? 0).toLocaleString()} pages.`
    );
  }

  /**
   * Where the issue is, in words.
   *
   * Three genuinely different answers, and the middle one is the reason the
   * backend computes concentration at all: "242 pages" tells an operator how
   * big the job is, "mostly under /blog" tells them which template to open. The
   * hedge in "mostly" is load-bearing — a path prefix is a proxy for a template
   * and the copy must not promise more than a proxy can deliver.
   */
  reach(issue: SiteWideTechnicalIssue): string {
    if (issue.pageShare >= 0.99) return 'every page';
    if (issue.concentratedIn) {
      const all = issue.concentratedPageCount === issue.pageCount;
      return `${all ? 'all' : 'mostly'} under ${issue.concentratedIn}`;
    }
    return 'across the site';
  }

  /** Sample paths, as a hover on the count, so a row can be checked. */
  samplesTitle(issue: SiteWideTechnicalIssue): string | null {
    if (!issue.samplePaths.length) return null;
    const more = issue.pageCount - issue.samplePaths.length;
    const shown = issue.samplePaths.join('\n');
    return more > 0 ? `${shown}\n+${more.toLocaleString()} more` : shown;
  }

  /**
   * Warnings are labelled; criticals are not.
   *
   * Most rows here are critical, so a badge on every one is a column of
   * identical words. Marking only the exception is what makes it readable.
   */
  isWarning(issue: SiteWideTechnicalIssue): boolean {
    return issue.severity === 'warning';
  }
}
